import {
  GeorefData,
  RegistrationRequest,
  RegistrationRequestType,
  UploadData,
} from '../../../../../../store/uploadStore';
import { Customer, ScheduledFlight, Site } from '../../../../../../types';
import React from 'react';
import { useAppStore } from '../../../../../../store/appStore';
import { ConfirmedUploadDate, confirmUploadDate } from './confirmUploadDate';
import { UploadType } from '../../../../../../consts/uploadType';
import { FlightDatePickerCancelledError } from '../confirmFlightDate/FlightDatePickerCancelledError';
import getPilotApiService from '../../../../../../api_services/PilotApiService';
import { generateUploadDataStateFromCreateResponse } from './generateUploadData';
import { trackUpload } from './trackUpload';
import { generateQueuedIntakeFileInfoState } from './generateQueuedIntakeFileInfoState';
import { generateCreatePayloadPhotosObject } from './generateCreatePayloadPhotosObject';
import Logger from '../../../../../../libs/logger/Logger';
import { calcHash } from '../../../../../../libs/file';
import { calculateTotal, filterDuplicateFiles, filterNewFiles, filterNonUploadedFiles } from './filterFiles';
import { UploadNotRestoredError } from '../../../../../../errors/UploadNotRestoredError';
import { intakeLoop } from '../../../../../../modules/intakeLoop/IntakeLoop';
import { GeorefNotSetError } from '../../../../../../errors/GeorefNotSetError';
import { isCancelledByUserError } from '../../../../../../errors/createCanceledError';
import * as Sentry from '@sentry/browser';
import { FileWithPath } from 'react-dropzone';
import { cacheUploadSize } from '../../../../../../store/optimizations/cacheUploadSize';
import { AlertData, useAlertStore } from '../../../../../../store/alertStore';
import { getTimezoneFromCoordinates } from '../../../../../../libs/getTimezoneFromCoordinates';
import { substrDateFromAirtableFormat } from '../../../helpers/date';
import { shouldSkipDateChecks } from '../../../../../../modules/intakeLoop/validate/validateImage';

// Important: We do not use immer due to performance and memory hits.
// Primitive values (type and currentBatch) are written into the "meta" object of the upload, so they are shared
// between different event handlers, as intended.

export const useHandleFileAdd = (
  uploadType: UploadType,
  customer: Customer,
  site: Site,
  schedule: ScheduledFlight | undefined,
  upload: UploadData | undefined,
  setUpload: (uploadData: UploadData) => void,
  setGeoref: (georefData: GeorefData) => void,
  georef: GeorefData | undefined,
  projectDate: string | undefined,
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined,
  addRegistrationRequest: (promise: Promise<unknown>, registrationRequests: RegistrationRequest) => void,
  removeRegistrationRequest: (promise: Promise<unknown>) => void,
) => {
  const { addAlert } = useAlertStore();
  const showWindow = useAppStore((state) => state.showWindow);
  const hideWindow = useAppStore((state) => state.hideWindow);

  return (files: FileWithPath[]) => {
    (async () => {
      const uniqueFileList = filterDuplicateFiles(files);
      const nonUploadedFiles = filterNonUploadedFiles(uniqueFileList, upload, registrationRequests);
      const newFiles = filterNewFiles(uniqueFileList, upload, registrationRequests);
      const newTotal = calculateTotal(uniqueFileList, upload, registrationRequests);

      if (!nonUploadedFiles.length) {
        Logger.log('Pilot selected all the same files');
        return;
      }

      trackUpload(uploadType, customer, site);

      const abortController = upload ? upload.meta.abortController : new AbortController();
      let currentUpload = upload ? upload : null;

      try {
        if (!currentUpload) {
          /**
           * Determine and confirm (if necessary) the upload date
           */
          const confirmed = await confirmUploadDateIfNeeded(
            abortController,
            uploadType,
            customer,
            site,
            schedule,
            files,
            projectDate,
            showWindow,
            hideWindow,
            addRegistrationRequest,
            removeRegistrationRequest,
          );
          if (!confirmed) return;

          /**
           * Create a new upload
           */
          const uploadResult = await createNewUpload(
            abortController,
            uploadType,
            customer,
            site,
            schedule,
            confirmed,
            addRegistrationRequest,
            removeRegistrationRequest,
            addAlert,
          );
          currentUpload = uploadResult.currentUpload;
          setUpload({ ...currentUpload });
        }

        if (!currentUpload) {
          throw new Error("The upload hasn't been registered");
        }

        if (!currentUpload.upload) {
          throw new UploadNotRestoredError();
        }

        /**
         * Register new files to the upload, put them into the upload queue and update total photo count
         */
        currentUpload.meta.currentBatch++;
        currentUpload.meta.isPreviouslyCompleted = false;

        // Timezone is required for validation
        currentUpload.meta.timezone = await getTimezoneFromCoordinates(customer.id + '/' + site.id, site.boundary);

        // We will update the counter right away, the UI will explain that it is stuck due to batch registration
        currentUpload.overview.photosCount.all = newTotal;
        setUpload({ ...currentUpload });

        const filesToQueue = generateQueuedIntakeFileInfoState(nonUploadedFiles, currentUpload.meta.currentBatch);

        if (newFiles.length) {
          await registerGeorefIfNecessary(
            customer,
            site,
            currentUpload,
            georef,
            setGeoref,
            addRegistrationRequest,
            removeRegistrationRequest,
          );

          await registerFilesToUpload(
            newFiles,
            abortController,
            currentUpload,
            customer,
            site,
            addRegistrationRequest,
            removeRegistrationRequest,
          );
        }

        currentUpload.queue.push(...filesToQueue);
        setUpload({ ...currentUpload });
        intakeLoop.runLoop();
      } catch (e: unknown) {
        // Cancelled from the UI
        if (isCancelledByUserError(e)) return null;

        if (currentUpload) {
          currentUpload.meta.error = `${e}`;
          setUpload({ ...currentUpload });
        }

        Sentry.captureException(e);
      }
    })();
  };
};

const confirmUploadDateIfNeeded = async (
  abortController: AbortController,
  uploadType: UploadType,
  customer: Customer,
  site: Site,
  schedule: ScheduledFlight | undefined,
  files: FileWithPath[],
  projectDate: string | undefined,
  showWindow: (component: React.ReactNode) => void,
  hideWindow: () => void,
  addRegistrationRequest: (promise: Promise<unknown>, registrationRequests: RegistrationRequest) => void,
  removeRegistrationRequest: (promise: Promise<unknown>) => void,
): Promise<ConfirmedUploadDate | null> => {
  if (!schedule) {
    // Self-service: the pilot picked the date themselves and metadata never overrides it.
    return { date: projectDate! };
  }

  if (shouldSkipDateChecks(customer.id)) {
    return { date: substrDateFromAirtableFormat(schedule.flightDateIso) };
  }

  // If there is a schedule, we will match the first picked image exif date with the schedule (and prompt the date if they are different)
  const determineAndConfirmPromise = confirmUploadDate(
    abortController.signal,
    uploadType,
    customer,
    site,
    schedule,
    files,
    showWindow,
    hideWindow,
  );
  addRegistrationRequest(determineAndConfirmPromise, {
    type: RegistrationRequestType.dateConfirmation,
    abortController,
  });
  try {
    return await determineAndConfirmPromise;
  } catch (e) {
    if (isCancelledByUserError(e)) return null;
    if (e instanceof FlightDatePickerCancelledError) return null;
    throw e;
  } finally {
    removeRegistrationRequest(determineAndConfirmPromise);
  }
};

/**
 * Registers the upload with POST request and adds it to the store
 */
const createNewUpload = async (
  abortController: AbortController,
  uploadType: UploadType,
  customer: Customer,
  site: Site,
  schedule: ScheduledFlight | undefined,
  confirmed: ConfirmedUploadDate,
  addRegistrationRequest: (promise: Promise<unknown>, registrationRequests: RegistrationRequest) => void,
  removeRegistrationRequest: (promise: Promise<unknown>) => void,
  addAlert: (data: AlertData) => void,
) => {
  const { date: confirmedDate, pilotConfirmedDate, expectedPhotoDate } = confirmed;

  const registerUploadPromise = getPilotApiService().startIntakeFlow(
    abortController.signal,
    customer.id,
    site.id,
    uploadType,
    confirmedDate,
    schedule?.airtableRecordId,
    pilotConfirmedDate,
    expectedPhotoDate,
  );
  addRegistrationRequest(registerUploadPromise, {
    type: RegistrationRequestType.uploadRegistration,
    abortController,
  });
  try {
    const { data } = await registerUploadPromise;
    const currentUpload = generateUploadDataStateFromCreateResponse(uploadType, data.upload, abortController, site);
    return { currentUpload };
  } catch (e: unknown) {
    if (!isCancelledByUserError(e)) {
      addAlert({
        type: 'warning-accent',
        title: `Couldn't register upload for ${customer.name} ${site.name} ${confirmedDate}`,
      });
    }
    throw e;
  } finally {
    removeRegistrationRequest(registerUploadPromise);
  }
};

/**
 * Registers new files to an existing upload via a PUT request
 */
const registerFilesToUpload = async (
  newFiles: FileWithPath[],
  abortController: AbortController,
  currentUpload: UploadData,
  customer: Customer,
  site: Site,
  addRegistrationRequest: (promise: Promise<unknown>, registrationRequests: RegistrationRequest) => void,
  removeRegistrationRequest: (promise: Promise<unknown>) => void,
) => {
  const filesToRegister = generateQueuedIntakeFileInfoState(newFiles, currentUpload.meta.currentBatch);

  const registerBatchPromise = getPilotApiService().updateIntakeFlow(
    abortController.signal,
    customer.id,
    site.id,
    currentUpload.meta.type,
    currentUpload.overview.date,
    {
      photos: generateCreatePayloadPhotosObject(filesToRegister),
    },
    currentUpload.overview.flightScheduleId,
  );

  addRegistrationRequest(registerBatchPromise, {
    type: RegistrationRequestType.batchRegistration,
    abortController,
    fileHashes: newFiles.map((file) => calcHash(file)),
    filesSizeTotal: newFiles.reduce((acc, { size }) => acc + (size ?? 0), 0),
  });

  cacheUploadSize(currentUpload, true);

  try {
    await registerBatchPromise;
  } finally {
    removeRegistrationRequest(registerBatchPromise);
  }
};

const registerGeorefIfNecessary = async (
  customer: Customer,
  site: Site,
  upload: UploadData,
  georef: GeorefData | undefined,
  setGeoref: (georefData: GeorefData) => void,
  addRegistrationRequest: (promise: Promise<unknown>, registrationRequests: RegistrationRequest) => void,
  removeRegistrationRequest: (promise: Promise<unknown>) => void,
) => {
  if (upload.meta.type === UploadType.Ppk) {
    if (!georef) throw new GeorefNotSetError();
    if (georef.meta.isRegistrationRequestSent) return;

    georef.meta.isRegistrationRequestSent = true;
    setGeoref({ ...georef });

    const fullUploadPath = `${customer.id}/${site.id}/${georef.meta.georefUploadPath}`;

    const georefRegistrationPromise = getPilotApiService().setGeoref(
      upload.meta.abortController.signal,
      customer.id,
      site.id,
      upload.overview.date,
      fullUploadPath,
      upload.overview.flightScheduleId,
    );

    addRegistrationRequest(georefRegistrationPromise, {
      type: RegistrationRequestType.ppkGeorefRegistration,
      abortController: georef.meta.abortController,
    });

    try {
      await georefRegistrationPromise;
    } finally {
      removeRegistrationRequest(georefRegistrationPromise);
    }
  }
};
